import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Loader2, Shield, AlertTriangle, TrendingDown, Clock, User, ArrowDownCircle, ArrowUpCircle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanAuditPage = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [onlyDestructive, setOnlyDestructive] = useState(false);

  useEffect(() => { fetchAll(); }, [onlyDestructive]); // eslint-disable-line

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [logsRes, sumRes] = await Promise.all([
        axios.get(`${API}/admin/plan-audit`, { params: { limit: 200, only_destructive: onlyDestructive } }),
        axios.get(`${API}/admin/plan-audit/summary`),
      ]);
      setLogs(logsRes.data.logs || []);
      setSummary(sumRes.data.top_affected_plans || []);
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-ES') : '';

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="plan-audit-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" /> Auditoría de Planes
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Registro de modificaciones a planes. Útil para rastrear cambios inesperados.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch id="destructive" checked={onlyDestructive} onCheckedChange={setOnlyDestructive}
                data-testid="filter-destructive" />
              <Label htmlFor="destructive" className="text-sm cursor-pointer">
                Solo cambios que <strong className="text-red-400">perdieron ejercicios</strong>
              </Label>
            </div>
            <Button size="sm" variant="outline" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refrescar
            </Button>
          </div>
        </div>

        {/* Resumen planes afectados 7d */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              Planes que perdieron ejercicios (últimos 7 días)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : summary.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Ningún plan perdió ejercicios en los últimos 7 días 🎉</p>
            ) : (
              <div className="space-y-2">
                {summary.map(s => (
                  <div key={s.plan_id} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">{s.plan_name || '(sin nombre)'}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {s.events_count} evento{s.events_count !== 1 ? 's' : ''} · por {s.last_user || '?'} · último: {fmtDate(s.last_event_at)}
                      </p>
                      {s.last_removed?.length > 0 && (
                        <p className="text-xs text-red-400 mt-1 truncate">
                          Quitados: {s.last_removed.slice(0, 5).join(' · ')}
                          {s.last_removed.length > 5 ? ' ...' : ''}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="border-red-500/40 text-red-400 ml-2">
                      -{s.total_exercises_lost} ejercicios
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Log completo */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base">Historial completo ({logs.length} eventos)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : logs.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Sin eventos</p>
            ) : (
              <div className="space-y-2">
                {logs.map(log => {
                  const isNegative = log.delta < 0;
                  const isPositive = log.delta > 0;
                  return (
                    <div key={log.id}
                      className={`p-3 rounded border ${isNegative ? 'bg-red-500/5 border-red-500/20' : isPositive ? 'bg-green-500/5 border-green-500/20' : 'bg-zinc-900/50 border-zinc-800'}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-foreground text-sm truncate">{log.plan_name || '(sin nombre)'}</p>
                            <Badge variant="outline" className="text-xs">{log.endpoint}</Badge>
                            <Badge variant="outline" className={`text-xs ${log.user_role === 'trainer' ? 'border-blue-500/40 text-blue-400' : 'border-amber-500/40 text-amber-400'}`}>
                              {log.user_role}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><User className="h-3 w-3" />{log.user_name || '?'}</span>
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(log.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 text-xs">
                            <span className="text-muted-foreground">{log.prev_exercise_count} → {log.new_exercise_count} ejercicios</span>
                            <span className="text-muted-foreground">· {log.prev_weeks} → {log.new_weeks} semanas</span>
                          </div>
                          {log.removed_exercise_names?.length > 0 && (
                            <p className="text-xs text-red-400 mt-1.5 truncate">
                              <ArrowDownCircle className="h-3 w-3 inline mr-1" />
                              Quitados: {log.removed_exercise_names.slice(0, 10).join(' · ')}
                              {log.removed_exercise_names.length > 10 ? ` (+${log.removed_exercise_names.length - 10})` : ''}
                            </p>
                          )}
                          {log.added_exercise_names?.length > 0 && (
                            <p className="text-xs text-green-400 mt-1 truncate">
                              <ArrowUpCircle className="h-3 w-3 inline mr-1" />
                              Añadidos: {log.added_exercise_names.slice(0, 10).join(' · ')}
                              {log.added_exercise_names.length > 10 ? ` (+${log.added_exercise_names.length - 10})` : ''}
                            </p>
                          )}
                        </div>
                        <Badge variant="outline" className={isNegative ? 'border-red-500/40 text-red-400' : isPositive ? 'border-green-500/40 text-green-400' : 'border-zinc-700 text-muted-foreground'}>
                          {isNegative && <TrendingDown className="h-3 w-3 mr-1" />}
                          {log.delta > 0 ? '+' : ''}{log.delta}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PlanAuditPage;
